import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import type { UserRole, UserCluster } from '../types';
import AmplifierIcon from './icons/AmplifierIcon';
import GuitarIcon from './icons/GuitarIcon';
import BriefcaseIcon from './icons/BriefcaseIcon';
import SpeakerIcon from './icons/SpeakerIcon';
import SchoolIcon from './icons/SchoolIcon';
import SparklesIcon from './icons/SparklesIcon';
import HeartIcon from './icons/HeartIcon';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import CheckIcon from './icons/CheckIcon';

const CLUSTER_DEFS: { id: UserCluster; label: string; sub: string; icon: React.ReactNode; roles: UserRole[] }[] = [
    {
        id: 'talent',
        label: 'Talent',
        sub: 'Artists, musicians, actors.',
        icon: <GuitarIcon className="h-6 w-6" />,
        roles: ['Musician', 'Singer', 'DJ', 'Actor', 'Director', 'Dancer', 'Performer', 'Choreographer', 'Model', 'Comedian']
    },
    {
        id: 'business',
        label: 'Business',
        sub: 'Venues, festivals, agencies.',
        icon: <BriefcaseIcon className="h-6 w-6" />,
        roles: ['Venue', 'Live Club', 'Theatre', 'Cinema', 'Festival', 'Label', 'Radio', 'Movie/TV Production', 'Casting Agency', 'Event Agency', 'Publisher', 'Producer']
    },
    {
        id: 'service',
        label: 'Services',
        sub: 'Audio, light, production.',
        icon: <SpeakerIcon className="h-6 w-6" />,
        roles: ['Audio Service', 'Recording Studio', 'Music Store', 'Luthier', 'Sound Engineer', 'Light Designer', 'Filmmaker / Videographer', 'Photographer', 'Make-Up Artist (MUA)', 'Costume Designer / Stylist', 'Backline Rental', 'Graphic Designer']
    },
    {
        id: 'edu',
        label: 'Education',
        sub: 'Academies and coaching.',
        icon: <SchoolIcon className="h-6 w-6" />,
        roles: ['Music School', 'Acting Academy', 'Dance School', 'Cultural Association', 'Vocal Coach']
    },
    {
        id: 'fan',
        label: 'Fan',
        sub: 'Enthusiasts and audience.',
        icon: <HeartIcon className="h-6 w-6" />,
        roles: ['Music Enthusiast', 'Music Addict', 'Live Music Fan', 'Concert Goer']
    }
];

type OnboardingStep = 'main' | 'roles';

const OnboardingPage: React.FC = () => {
    const { currentUser, completeOnboarding } = useAuth();
    const [selectedRoles, setSelectedRoles] = useState<UserRole[]>([]);
    const [referralCode, setReferralCode] = useState('');
    const [chosenHandle, setChosenHandle] = useState(currentUser?.handle || '');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Step navigation
    const [step, setStep] = useState<OnboardingStep>('main');
    const [activeClusterId, setActiveClusterId] = useState<UserCluster | null>(null);

    useEffect(() => {
        if (currentUser?.handle) setChosenHandle(currentUser.handle);
    }, [currentUser]);

    const activeCluster = CLUSTER_DEFS.find(c => c.id === activeClusterId);

    const toggleRole = (role: UserRole) => {
        setSelectedRoles(prev => 
            prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
        );
    };

    const handleClusterClick = (id: UserCluster) => {
        setActiveClusterId(id);
        setStep('roles');
    };

    const handleSubmit = async () => {
        if (selectedRoles.length === 0) {
            setError('Please select at least one role to continue.');
            return;
        }
        if (chosenHandle.length < 3) {
            setError('Handle must be at least 3 characters.');
            return;
        }

        setIsLoading(true);
        setError('');
        try {
            await completeOnboarding(selectedRoles, referralCode, chosenHandle);
        } catch (err) {
            setError(`Onboarding failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
            setIsLoading(false);
        }
    };

    const renderMainStep = () => (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-2xl mx-auto">
            <div className="text-center mb-6">
                <h2 className="text-2xl font-black text-white uppercase tracking-tighter">Who are you?</h2>
                <p className="text-brand-muted text-xs uppercase font-bold tracking-widest mt-1">Select your professional cluster to begin</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                {CLUSTER_DEFS.filter(c => c.id !== 'fan').map(cluster => {
                    const selectedInCluster = cluster.roles.filter(r => selectedRoles.includes(r)).length;
                    return (
                        <button
                            key={cluster.id}
                            onClick={() => handleClusterClick(cluster.id)}
                            className={`group relative bg-gray-800/40 border-2 rounded-2xl p-4 text-center transition-all hover:bg-gray-800/60 active:scale-[0.98] ${
                                selectedInCluster > 0 ? 'border-brand-orange' : 'border-gray-700'
                            }`}
                        >
                            <div className={`flex flex-col items-center gap-2 ${selectedInCluster > 0 ? 'text-brand-orange' : 'text-brand-muted group-hover:text-white'}`}>
                                {cluster.icon}
                                <h3 className="text-xs font-black uppercase tracking-widest">{cluster.label}</h3>
                            </div>
                            {selectedInCluster > 0 && (
                                <div className="absolute -top-2 -right-2 bg-brand-orange text-white text-[9px] font-black w-5 h-5 flex items-center justify-center rounded-full uppercase shadow-lg">
                                    {selectedInCluster}
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="text-center py-4">
                <div className="flex items-center justify-center space-x-4 mb-4">
                    <div className="h-px bg-gray-800 flex-1"></div>
                    <p className="text-[9px] font-black text-brand-muted uppercase tracking-[0.3em]">Or maybe you are a music lover</p>
                    <div className="h-px bg-gray-800 flex-1"></div>
                </div>
                <button
                    onClick={() => handleClusterClick('fan')}
                    className={`inline-flex items-center gap-3 px-6 py-3 rounded-full border-2 transition-all group ${
                        selectedRoles.some(r => CLUSTER_DEFS.find(c => c.id === 'fan')?.roles.includes(r))
                        ? 'bg-pink-600/10 border-pink-500 text-white'
                        : 'bg-gray-800/20 border-gray-800 hover:border-pink-500 text-brand-muted hover:text-white'
                    }`}
                >
                    <HeartIcon className={`h-5 w-5 ${selectedRoles.some(r => CLUSTER_DEFS.find(c => c.id === 'fan')?.roles.includes(r)) ? 'fill-current text-pink-500' : 'text-pink-500/40 group-hover:text-pink-500'}`} />
                    <span className="font-black uppercase tracking-widest text-[10px]">Join the Audience</span>
                </button>
            </div>

            <div className="bg-brand-surface border border-gray-800 rounded-2xl p-5 shadow-xl space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-[9px] font-black text-brand-muted tracking-widest mb-1.5 uppercase">Unique Handle</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-orange font-black">@</span>
                            <input 
                                type="text"
                                value={chosenHandle}
                                onChange={(e) => setChosenHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                                className="w-full bg-gray-900 border border-gray-700 text-white pl-8 pr-4 py-3 rounded-xl focus:border-brand-orange outline-none transition-colors font-mono text-sm font-bold"
                                placeholder="handle"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-[9px] font-black text-brand-muted tracking-widest mb-1.5 flex items-center gap-2 uppercase">
                            <SparklesIcon className="h-3 w-3 text-brand-orange" />
                            Scout Code
                        </label>
                        <input 
                            type="text"
                            value={referralCode}
                            onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                            placeholder="OPTIONAL"
                            className="w-full bg-gray-900 border border-gray-700 text-brand-orange text-center font-mono font-bold py-3 rounded-xl focus:border-brand-orange outline-none transition-colors placeholder:text-gray-700 text-sm"
                        />
                    </div>
                </div>
                
                {selectedRoles.length > 0 && (
                    <div className="pt-4 border-t border-gray-800">
                        <label className="block text-[9px] font-black text-brand-muted tracking-widest mb-3 uppercase">Selected Identities ({selectedRoles.length})</label>
                        <div className="flex flex-wrap gap-2">
                            {selectedRoles.map(role => (
                                <span key={role} className="bg-brand-orange/10 text-brand-orange border border-brand-orange/20 text-[9px] font-black px-2.5 py-1.5 rounded-lg flex items-center gap-2 uppercase tracking-widest">
                                    {role}
                                    <button onClick={() => toggleRole(role)} className="hover:text-white bg-brand-orange/20 rounded-full w-4 h-4 flex items-center justify-center text-xs">×</button>
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="pt-2">
                <button
                    onClick={handleSubmit}
                    disabled={selectedRoles.length === 0 || isLoading}
                    className="w-full bg-brand-orange text-white font-black py-4 rounded-full shadow-2xl hover:bg-orange-600 disabled:opacity-30 transition-all text-xs uppercase tracking-[0.2em]"
                >
                    {isLoading ? "Validating Access..." : "Enter Application"}
                </button>
                {error && <p className="text-red-400 text-[10px] text-center mt-3 font-black uppercase tracking-widest">{error}</p>}
            </div>
        </div>
    );

    const renderRolesStep = () => (
        <div className="animate-in fade-in slide-in-from-right-4 duration-500 max-w-3xl mx-auto">
            <button 
                onClick={() => setStep('main')}
                className="mb-6 flex items-center space-x-2 text-brand-muted hover:text-white font-black uppercase tracking-widest text-[9px] transition-colors"
            >
                <ArrowLeftIcon className="h-4 w-4" />
                <span>Change Category</span>
            </button>

            <div className="bg-brand-surface border border-gray-800 rounded-3xl p-6 sm:p-8 shadow-2xl">
                <div className="flex items-center space-x-4 mb-8">
                    <div className="bg-brand-orange/10 p-3 rounded-2xl text-brand-orange">
                        {activeCluster?.icon}
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-white tracking-tighter uppercase">{activeCluster?.label}</h2>
                        <p className="text-brand-muted font-bold uppercase tracking-widest text-[9px]">What is your role in this cluster?</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                    {activeCluster?.roles.map(role => {
                        const isSelected = selectedRoles.includes(role);
                        return (
                            <button
                                key={role}
                                onClick={() => toggleRole(role)}
                                className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all font-black uppercase tracking-widest text-[9px] ${
                                    isSelected
                                    ? 'bg-brand-orange/10 border-brand-orange text-white shadow-lg shadow-brand-orange/5'
                                    : 'bg-gray-900 border-gray-700 text-brand-muted hover:border-gray-500 hover:text-white'
                                }`}
                            >
                                <span className="truncate mr-2">{role}</span>
                                {isSelected && <CheckIcon className="h-3.5 w-3.5 text-brand-orange flex-shrink-0" />}
                            </button>
                        );
                    })}
                </div>

                <div className="mt-10 flex justify-center sm:justify-end">
                    <button
                        onClick={() => setStep('main')}
                        className="w-full sm:w-auto bg-white text-black font-black px-12 py-3.5 rounded-full hover:bg-gray-200 transition-all text-[10px] uppercase tracking-widest shadow-xl"
                    >
                        Confirm {activeCluster?.label} Roles
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="bg-brand-dark min-h-screen flex items-center justify-center p-4 font-urbanist">
            <div className="w-full">
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center text-brand-orange space-x-2">
                        <AmplifierIcon className="h-10 w-10" />
                        <h1 className="text-3xl font-black text-white tracking-tighter uppercase">beYourAMP</h1>
                    </div>
                </div>

                {step === 'main' ? renderMainStep() : renderRolesStep()}
            </div>
        </div>
    );
};

export default OnboardingPage;